import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Clock, CheckCircle, Truck, XCircle, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const STATUSES = ['pending','confirmed','printing','shipped','delivered','cancelled'] as const;
type Status = typeof STATUSES[number];

const STATUS_CONFIG: Record<Status, { label: string; color: string; icon: any }> = {
  pending:   { label: 'Oczekuje',     color: 'oklch(0.72 0.24 45)',   icon: Clock },
  confirmed: { label: 'Potwierdzone', color: 'oklch(0.65 0.32 280)',  icon: CheckCircle },
  printing:  { label: 'Drukowanie',   color: 'oklch(0.68 0.28 195)',  icon: Package },
  shipped:   { label: 'Wysłane',      color: 'oklch(0.65 0.26 150)',  icon: Truck },
  delivered: { label: 'Dostarczone',  color: 'oklch(0.65 0.26 150)',  icon: CheckCircle },
  cancelled: { label: 'Anulowane',    color: 'oklch(0.62 0.28 15)',   icon: XCircle },
};

interface Order {
  id: string; status: Status; total_pln: number; created_at: string; updated_at: string;
  full_name: string; email: string; phone: string | null;
  street: string; city: string; postal_code: string; country: string; notes: string | null;
  items_count: number; username: string | null;
  items?: { id: string; title: string; price_pln: number; quantity: number; image_url: string | null }[];
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('orders_overview')
      .select('*')
      .order('created_at', { ascending: false });
    setOrders((data ?? []) as Order[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const loadItems = async (orderId: string) => {
    if (expanded === orderId) { setExpanded(null); return; }
    const { data } = await supabase.from('order_items')
      .select('id, title, price_pln, quantity, image_url')
      .eq('order_id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, items: data ?? [] } : o));
    setExpanded(orderId);
  };

  const changeStatus = async (orderId: string, status: Status) => {
    setUpdating(orderId);
    const { error } = await supabase.from('orders').update({ status }).eq('id', orderId);
    if (error) { toast.error('Błąd zmiany statusu'); }
    else { toast.success(`Status zmieniony: ${STATUS_CONFIG[status].label}`); await load(); }
    setUpdating(null);
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: orders.filter(o => o.status === s).length }), {} as Record<Status, number>);

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>ZAMÓWIENIA</h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            Zarządzaj zamówieniami klientów SOKAO-TOYS
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          style={{ borderColor: 'oklch(0.25 0.045 280)', fontFamily: 'Rajdhani, sans-serif' }}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Odśwież
        </button>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')}
          className="px-3 py-1.5 rounded-full text-xs font-bold transition-all border"
          style={{ fontFamily: 'JetBrains Mono, monospace',
            background: filter === 'all' ? 'var(--primary)' : 'oklch(0.16 0.03 280)',
            color: filter === 'all' ? 'white' : 'var(--muted-foreground)',
            borderColor: filter === 'all' ? 'var(--primary)' : 'oklch(0.25 0.045 280)' }}>
          WSZYSTKIE ({orders.length})
        </button>
        {STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          return (
            <button key={s} onClick={() => setFilter(s)}
              className="px-3 py-1.5 rounded-full text-xs font-bold transition-all border"
              style={{ fontFamily: 'JetBrains Mono, monospace',
                background: filter === s ? `${cfg.color}25` : 'oklch(0.16 0.03 280)',
                color: filter === s ? cfg.color : 'var(--muted-foreground)',
                borderColor: filter === s ? cfg.color : 'oklch(0.25 0.045 280)' }}>
              {cfg.label.toUpperCase()} ({counts[s] ?? 0})
            </button>
          );
        })}
      </div>

      {/* Orders list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-3 text-muted-foreground">
          <Package className="w-12 h-12 opacity-20" />
          <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>Brak zamówień w tej kategorii</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order, i) => {
            const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
            const Icon = cfg.icon;
            const isExp = expanded === order.id;
            return (
              <motion.div key={order.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-2xl border overflow-hidden"
                style={{ background: 'oklch(0.15 0.03 280)', borderColor: 'oklch(0.23 0.04 280)' }}>

                {/* Row header */}
                <div className="flex items-center gap-3 px-5 py-4 cursor-pointer flex-wrap"
                  onClick={() => loadItems(order.id)}>
                  <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                    style={{ background: `${cfg.color}20` }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: cfg.color }}>
                        {cfg.label.toUpperCase()}
                      </span>
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        #{order.id.slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground font-semibold mt-0.5" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                      {order.full_name}
                      {order.username && <span className="text-muted-foreground ml-1">@{order.username}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {new Date(order.created_at).toLocaleDateString('pl-PL')}
                      {' · '}{order.items_count} poz.
                      {' · '}{order.city}
                    </p>
                  </div>
                  <span className="font-black text-lg flex-shrink-0"
                    style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--primary)' }}>
                    {Number(order.total_pln).toFixed(2)} zł
                  </span>
                  {isExp ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>

                {/* Expanded detail */}
                {isExp && (
                  <div className="border-t px-5 py-4 space-y-4"
                    style={{ borderColor: 'oklch(0.22 0.04 280)' }}>

                    {/* Address + contact */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="space-y-1">
                        <p className="text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ADRES</p>
                        <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>{order.street}</p>
                        <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>{order.postal_code} {order.city}, {order.country}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>KONTAKT</p>
                        <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>{order.email}</p>
                        {order.phone && <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>{order.phone}</p>}
                      </div>
                    </div>

                    {/* Items */}
                    {order.items && order.items.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>POZYCJE</p>
                        {order.items.map(item => (
                          <div key={item.id} className="flex items-center gap-3">
                            {item.image_url && (
                              <img src={item.image_url} alt={item.title} className="w-9 h-12 object-cover rounded-lg flex-shrink-0" />
                            )}
                            <span className="flex-1 text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                              {item.title} ×{item.quantity}
                            </span>
                            <span className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--primary)' }}>
                              {(Number(item.price_pln) * item.quantity).toFixed(2)} zł
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Status change */}
                    <div>
                      <p className="text-xs font-bold tracking-widest text-muted-foreground mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ZMIEŃ STATUS</p>
                      <div className="flex flex-wrap gap-2">
                        {STATUSES.map(s => {
                          const c = STATUS_CONFIG[s];
                          const isActive = order.status === s;
                          return (
                            <button key={s} disabled={isActive || updating === order.id}
                              onClick={() => changeStatus(order.id, s)}
                              className="px-3 py-1.5 rounded-full text-xs font-bold transition-all border disabled:opacity-50"
                              style={{ fontFamily: 'JetBrains Mono, monospace',
                                background: isActive ? `${c.color}25` : 'oklch(0.18 0.035 280)',
                                color: isActive ? c.color : 'var(--muted-foreground)',
                                borderColor: isActive ? c.color : 'oklch(0.25 0.045 280)',
                                cursor: isActive ? 'default' : 'pointer' }}>
                              {c.label.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
